import { useState, useMemo, useEffect } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import { Grid } from '@/components/common/grid';
import { Spinner } from 'src/components/ui/spinner';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'src/components/ui/tooltip';
import {
  InfoIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import DataTable from 'src/md-examples/Tables/DataTable';
import type { ColumnDef } from 'src/lib/tanstack-table';

import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

import { useProduct } from 'src/api/inventory-management/products/useProduct';
import useFilteredProductAttributes from 'src/api/inventory-management/products/useFilteredProductAttributes';
import useFamousProductVariantMatches from 'src/api/erp-integrations/famous/useFamousProductVariantMatches';
import VirtualizedVariantList from './VirtualizedVariantList';
import { ProductsV2Autocomplete } from '@/components/products/ProductsV2Autocomplete';
import useCustomerProduct from '@/api/inventory-management/products/useCustomerProduct';
import SelectedVariantSummary from './SelectedVariantSummary';
import ProductVariantListItem from './ProductVariantListItem';
import useOrganizationConfig from '@/api/core/organization-config/useOrganizationConfig';
import { sortProductAttributesByOrder } from '@/api/inventory-management/products/utils';
import { useProductVariants } from '@/api/inventory-management/products/useProductVariants';
import { useIsOneToOneProductMapping } from '@/hooks/useIsOneToOneProductMapping';
import { T, Num, Var, useGT } from 'gt-react';

const AssociateLineItemModal: React.FC<{
  lineItem: LineItem;
  editable?: boolean;
  onClose: (productVariantId?: string) => void;
  customerId: string;
}> = ({ lineItem, onClose, customerId }) => {
  const gt = useGT();
  const currentCustomerProductId =
    lineItem.selectedCustomerProductId || lineItem.suggestedCustomerProductId;

  const { data: currentCustomerProduct, error: currentCustomerProductError } =
    useCustomerProduct(currentCustomerProductId, !!currentCustomerProductId);

  const selectedVariantId =
    currentCustomerProduct?.customerProduct.productVariantId;
  const [selectedProductId, setSelectedProductId] = useState<string>(
    currentCustomerProduct?.customerProduct.productId ?? '',
  );

  // Track product attributes for variant matching
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  // Track selected enum option per product attribute id for filtered attributes API
  const [
    selectedAttributeOptionsByAttrId,
    setSelectedAttributeOptionsByAttrId,
  ] = useState<Record<string, string>>({});
  const [showAttributes, setShowAttributes] = useState<boolean>(true);

  const isOneToOneProduct = useIsOneToOneProductMapping();

  // Variant filter state
  const [variantFilter, setVariantFilter] = useState<string>('');

  // Fetch current product data
  const {
    data: currentProductData,
    isLoading: isLoadingCurrentProduct,
    error: currentProductError,
  } = useProduct(
    selectedProductId,
    { includes: ['attributes'] },
    !!selectedProductId,
  );

  // Organization config (contains optional attribute ordering schema)
  const { data: orgConfigData, isLoading: isLoadingOrgConfig } =
    useOrganizationConfig();

  // Fetch filtered attributes (remaining options) based on current enum selections
  const {
    data: filteredAttributesData,
    isLoading: isLoadingFilteredAttributes,
    error: filteredAttributesError,
  } = useFilteredProductAttributes(
    selectedProductId,
    {
      selectedAttributes:
        Object.keys(selectedAttributeOptionsByAttrId).length > 0
          ? selectedAttributeOptionsByAttrId
          : undefined,
      customerId,
    },
    !!selectedProductId,
  );

  // Get variant matches based on selected attributes (normal flow)
  const {
    data: variantMatchesData,
    isLoading: isLoadingVariants,
    error: variantMatchesError,
  } = useFamousProductVariantMatches(selectedProductId, {
    originAttributes:
      Object.keys(selectedAttributes).length > 0
        ? selectedAttributes
        : undefined,
    customerId,
  });

  // In 1:1 mode, fetch all variants for the product via generic endpoint
  const { data: allVariantsData, isLoading: isLoadingAllVariants } =
    useProductVariants(
      selectedProductId,
      isOneToOneProduct && !!selectedProductId,
    );

  // Auto-select the first variant when 1:1 mapping is active
  useEffect(() => {
    if (!isOneToOneProduct) return;
    if (!selectedProductId) return;
    if (!allVariantsData?.variants?.length) return;

    const firstVariant = allVariantsData.variants[0];
    if (firstVariant.id === selectedVariantId) return;

    onClose(firstVariant.id);
  }, [
    isOneToOneProduct,
    selectedProductId,
    allVariantsData?.variants,
    selectedVariantId,
    onClose,
  ]);

  const error =
    currentProductError ||
    filteredAttributesError ||
    variantMatchesError ||
    currentCustomerProductError;

  // Prefer filtered attributes when available; otherwise keep showing the last known list
  const [lastKnownAttributes, setLastKnownAttributes] = useState(
    currentProductData?.product?.attributes || [],
  );

  const rawAttributes = useMemo(() => {
    const next =
      filteredAttributesData?.attributes ||
      currentProductData?.product?.attributes ||
      lastKnownAttributes ||
      [];
    return next;
  }, [
    filteredAttributesData?.attributes,
    currentProductData?.product?.attributes,
    lastKnownAttributes,
  ]);

  // Resolve preferred order: product-specific > global
  const preferredOrder: string[] | null = useMemo(() => {
    const schema = orgConfigData?.organizationConfig?.productAttributeOrder;
    if (!schema) return null;
    const productId = selectedProductId || currentProductData?.product?.id;

    if (productId && schema.byProductId && schema.byProductId[productId]) {
      return schema.byProductId[productId];
    }
    return schema.global ?? null;
  }, [
    orgConfigData?.organizationConfig?.productAttributeOrder,
    selectedProductId,
    currentProductData?.product?.id,
  ]);

  // Update cache whenever we have a non-empty list
  useEffect(() => {
    if (rawAttributes && rawAttributes.length > 0) {
      setLastKnownAttributes(rawAttributes);
    }
  }, [rawAttributes]);

  const { visibleAttributes, hiddenAttributes } = useMemo(() => {
    const visible: typeof rawAttributes = [];
    const hidden: typeof rawAttributes = [];
    rawAttributes.forEach((attr) => {
      const enumOptions = attr.customField.coreEnum?.options || [];
      const noOptions =
        attr.customField.fieldType === 'enum' && enumOptions.length === 0;
      if (noOptions) hidden.push(attr);
      else visible.push(attr);
    });
    const sortedVisible = sortProductAttributesByOrder(visible, preferredOrder);
    return { visibleAttributes: sortedVisible, hiddenAttributes: hidden };
  }, [rawAttributes, preferredOrder]);

  // Fetch top product variant matches data
  const topVariantIds = useMemo(() => {
    if (!lineItem.topProductVariantMatches) return [];
    return lineItem.topProductVariantMatches
      .toSorted((a, b) => b.confidence - a.confidence)
      .slice(0, 5) // Show top 5 matches
      .map((match) => match.productVariantId);
  }, [lineItem.topProductVariantMatches]);

  // Memoize the column definitions to prevent re-creation on every render
  const lineItemColumns: ColumnDef<LineItem>[] = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'customerIdentifier',
        cell: ({ row }) => {
          const item = row.original;
          // TODO remove sku and upc
          const identifier = item.customerIdentifier || item.sku || item.upc;

          return identifier ? (
            <MDTypography variant="caption">{identifier}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          );
        },
      },
      {
        header: 'Item',
        accessorKey: 'customerDescription',
        cell: ({ row }) => {
          const item = row.original;

          // Try all possible descriptive fields in order of preference
          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            'No description available';

          return (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="caption" color="text.secondary">
                {description}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        header: 'Unit Price',
        accessorKey: 'unitPrice',
        cell: ({ row }) => {
          const value = row.original.unitPrice;
          return <>{value ? `$${value.toFixed(2)}` : ''}</>;
        },
      },
    ],
    [],
  );

  // Memoize the table data to prevent unnecessary re-renders
  const tableData = useMemo(
    () => ({
      columns: lineItemColumns,
      rows: [lineItem],
    }),
    [lineItem, lineItemColumns],
  );

  const handleVariantSelect = (productVariantId?: string | undefined) => {
    onClose(productVariantId);
  };

  const handleAttributeChange = (
    attributeName: string,
    value: string | null,
  ) => {
    const updatedAttributes = { ...selectedAttributes };

    if (value !== null && value !== undefined) {
      updatedAttributes[attributeName] = value;
    } else {
      delete updatedAttributes[attributeName];
    }

    setSelectedAttributes(updatedAttributes);
  };

  const handleEnumSelectionByAttrId = (
    productAttributeId: string,
    optionId: string | null,
  ) => {
    setSelectedAttributeOptionsByAttrId((prev) => {
      const next = { ...prev };
      if (optionId) {
        next[productAttributeId] = optionId;
      } else {
        delete next[productAttributeId];
      }
      return next;
    });
  };

  // Display matching variants information
  const renderMatchingVariantsInfo = () => {
    if (isLoadingVariants) {
      return (
        <div
          className="flex items-center mt-4 mb-2 p-3 rounded"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}
        >
          <Spinner size="sm" className="mr-1.5 text-blue-500" />
          <MDTypography variant="body2" color="text.secondary">
            Finding matching variants...
          </MDTypography>
        </div>
      );
    }

    if (variantMatchesError) {
      return (
        <div
          className="mt-4 mb-2 p-3 rounded"
          style={{ backgroundColor: 'rgba(211, 47, 47, 0.04)' }}
        >
          <MDTypography variant="body2" color="error">
            <T context="associate line item, variant load error">
              Error loading variants
            </T>
          </MDTypography>
        </div>
      );
    }

    if (
      variantMatchesData?.variants &&
      variantMatchesData.variants.length > 0
    ) {
      const variants = variantMatchesData.variants;

      // If there are too many variants, just show a count
      if (variants.length > 2500) {
        return (
          <div
            className="mt-4 mb-2 p-3 rounded"
            style={{
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              border: '1px solid rgba(25, 118, 210, 0.1)',
            }}
          >
            <div className="flex items-center">
              <InfoIcon size={18} className="mr-1.5 text-info-main" />
              <div>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  color="info.main"
                >
                  <T context="too many variant matches">
                    <Num>{variants.length}</Num> matching variants found
                  </T>
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  <T context="hint to narrow variant search">
                    Please refine your attribute selections to narrow down the
                    options
                  </T>
                </MDTypography>
              </div>
            </div>
          </div>
        );
      }

      // Show selectable variants list
      return (
        <div className="mt-4 mb-2">
          <div className="flex justify-between items-center mb-3 px-1">
            <div className="flex items-center">
              <CheckCircleIcon size={18} className="mr-1.5 text-success-main" />
              <MDTypography
                variant="body2"
                fontWeight="medium"
                color="success.main"
              >
                {variants.length === 1 ? (
                  <T context="single variant match">One match found</T>
                ) : (
                  <T context="multiple variant matches">
                    <Num>{variants.length}</Num> matching variants found
                  </T>
                )}
              </MDTypography>
            </div>

            {/* Filter input */}
            <div style={{ width: '300px' }} className="relative">
              <MagnifyingGlassIcon
                size={16}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
              <Input
                className="pl-7 text-sm"
                placeholder={gt('Filter matched variants...', {
                  context: 'filter variant list input',
                })}
                value={variantFilter}
                onChange={(e) => setVariantFilter(e.target.value)}
              />
            </div>
          </div>

          {(() => {
            // Filter variants based on the filter text
            const filteredVariants = variants.filter((variant) => {
              if (!variantFilter) return true;

              const filterLower = variantFilter.toLowerCase();

              // Filter by SKU
              if (variant.sku?.toLowerCase().includes(filterLower)) {
                return true;
              }

              // Filter by ID
              if (variant.id.toLowerCase().includes(filterLower)) {
                return true;
              }

              // Filter by NAMEINVC if available in originMetadata
              if (
                variant.originMetadata &&
                typeof variant.originMetadata === 'object'
              ) {
                const metadata = variant.originMetadata as unknown as {
                  NAMEINVC?: string;
                  PRODUCT_ID?: string;
                };
                if (
                  metadata.NAMEINVC &&
                  typeof metadata.NAMEINVC === 'string'
                ) {
                  if (metadata.NAMEINVC.toLowerCase().includes(filterLower)) {
                    return true;
                  }
                }
                if (
                  metadata.PRODUCT_ID &&
                  typeof metadata.PRODUCT_ID === 'string'
                ) {
                  if (metadata.PRODUCT_ID.toLowerCase().includes(filterLower)) {
                    return true;
                  }
                }
              }

              return false;
            });

            const countMessage =
              filteredVariants.length === 0 ? (
                <T context="variant filter, no results">
                  No variants match your filter
                </T>
              ) : filteredVariants.length === 1 ? (
                <T context="variant filter, one result">
                  1 variant matches your filter
                </T>
              ) : (
                <T context="variant filter, multiple results">
                  <Num>{filteredVariants.length}</Num> variants match your
                  filter
                </T>
              );

            return (
              <>
                {variantFilter && (
                  <div className="mb-3 -mt-3 px-1 text-right">
                    <MDTypography variant="caption" color="text.secondary">
                      {countMessage}
                    </MDTypography>
                  </div>
                )}

                <VirtualizedVariantList
                  variants={filteredVariants}
                  selectedVariantId={selectedVariantId}
                  onSelect={handleVariantSelect}
                  maxHeight={300}
                />
              </>
            );
          })()}
        </div>
      );
    }

    if (Object.keys(selectedAttributes).length > 0) {
      return (
        <div
          className="flex items-center mt-4 mb-2 p-3 rounded"
          style={{ backgroundColor: 'rgba(211, 47, 47, 0.04)' }}
        >
          <XCircleIcon size={18} className="mr-1.5 text-error-main" />
          <MDTypography variant="body2" color="error.main">
            <T context="no variants for selected attributes">
              No matching variants found with these attributes
            </T>
          </MDTypography>
        </div>
      );
    }

    return null;
  };

  // Display top matches section
  const renderTopMatches = () => {
    if (topVariantIds.length === 0) return null;

    return (
      <Grid item xs={12}>
        <MDBox mb={2}>
          <MDTypography variant="subtitle2" fontWeight="medium">
            <T context="suggested product variant matches">Top Matches</T>
          </MDTypography>
          <ul>
            {topVariantIds.map((variantId) => {
              return (
                <ProductVariantListItem
                  key={variantId}
                  variantId={variantId}
                  isSelected={selectedVariantId === variantId}
                  onSelect={handleVariantSelect}
                />
              );
            })}
          </ul>
        </MDBox>
      </Grid>
    );
  };

  return (
    <Modal
      maxWidth="lg"
      // Opens on top of the Sales Order Draft modal — elevate above it so the
      // nested content isn't tied with (and hidden behind) the parent.
      nested
      open={true}
      title={gt('Select Product', {
        context: 'modal title, associate line item to product',
      })}
      closeable
      error={error}
      hideCloseButton
      onCloseButton={{
        text: gt('Cancel', { context: 'modal dismiss button' }),
        onClick: () => {
          onClose();
        },
      }}
    >
      {/* Sticky Customer Request Section */}
      <MDBox
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
          mb: 2,
        }}
      >
        <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
          <T context="section, original line item from customer">
            Customer Request
          </T>
        </MDTypography>
        <MDBox
          p={2}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
        >
          <DataTable<LineItem> table={tableData} />
        </MDBox>
      </MDBox>

      {/* Scrollable Content */}
      <MDBox
        sx={{
          maxHeight: 'calc(70vh - 200px)', // Adjust based on your modal height
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Grid container spacing={2}>
          {/* Show currently selected product variant with clear visual indication */}
          <Grid item xs={12}>
            {selectedVariantId && (
              <SelectedVariantSummary variantId={selectedVariantId} />
            )}
          </Grid>

          {/* Top Product Variant Matches */}
          {renderTopMatches()}

          {/* Product Search */}
          <Grid item xs={12}>
            <MDBox mb={3}>
              <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                {selectedVariantId ? (
                  <T context="product picker heading">
                    Select a different product
                  </T>
                ) : topVariantIds.length === 0 ? (
                  <T context="product picker heading">Match to a product</T>
                ) : (
                  <T context="product picker heading">
                    Or Select Another Product
                  </T>
                )}
              </MDTypography>
              <ProductsV2Autocomplete
                className="w-fit"
                selectedProductId={selectedProductId}
                onProductSelect={(value) => {
                  if (value) {
                    setSelectedProductId(value);
                    setSelectedAttributes({});
                    setShowAttributes(true);
                  }
                }}
              />
            </MDBox>
          </Grid>

          {isOneToOneProduct ? (
            selectedProductId && isLoadingAllVariants ? (
              <Grid item xs={12} className="flex items-center py-4 px-2">
                <Spinner size="sm" className="mr-1.5" />
                <MDTypography variant="body2" color="text.secondary">
                  Selecting variant...
                </MDTypography>
              </Grid>
            ) : selectedProductId && !allVariantsData?.variants?.length ? (
              <Grid item xs={12} className="px-2 py-4">
                <MDTypography variant="body2" color="error">
                  <T context="1:1 product mapping, missing variant">
                    No variant found for this product.
                  </T>
                </MDTypography>
              </Grid>
            ) : null
          ) : isLoadingCurrentProduct ||
            isLoadingOrgConfig ||
            isLoadingFilteredAttributes ? (
            <Grid
              item
              xs={12}
              className="flex items-center justify-center py-4 w-full"
            >
              <Spinner className="mr-1" />
            </Grid>
          ) : selectedProductId ? (
            <Grid item xs={12}>
              {showAttributes && (
                <MDBox mb={2}>
                  {isLoadingCurrentProduct &&
                  !filteredAttributesData?.attributes ? (
                    <div className="flex justify-center p-4">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <MDBox sx={{ position: 'relative' }}>
                        {isLoadingFilteredAttributes && (
                          <div
                            role="presentation"
                            aria-busy
                            className="absolute inset-0 z-[2] cursor-progress pointer-events-auto"
                          />
                        )}
                        <Grid container spacing={2} className="mb-6">
                          {visibleAttributes.map((attr) => {
                            const enumOptions =
                              attr.customField.coreEnum?.options || [];
                            const noOptions =
                              attr.customField.fieldType === 'enum' &&
                              enumOptions.length === 0;
                            const isDisabled =
                              isLoadingFilteredAttributes || noOptions;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={attr.id}>
                                {attr.customField.fieldType === 'enum' &&
                                attr.customField.coreEnum ? (
                                  <div>
                                    <Label
                                      htmlFor={`attr-${attr.id}`}
                                      className="text-sm mb-1 block"
                                    >
                                      {attr.customField.fieldName}
                                      {attr.isRequired && ' *'}
                                    </Label>
                                    <ShadcnSelect
                                      disabled={isDisabled}
                                      value={
                                        selectedAttributeOptionsByAttrId[
                                          attr.id
                                        ] || ''
                                      }
                                      onValueChange={(value) => {
                                        if (!value) {
                                          handleEnumSelectionByAttrId(
                                            attr.id,
                                            null,
                                          );
                                          if (
                                            attr.customField.coreEnum &&
                                            attr.customField.coreEnum
                                              .originEntity
                                          ) {
                                            handleAttributeChange(
                                              attr.customField.coreEnum
                                                .originEntity,
                                              null,
                                            );
                                          }
                                        } else {
                                          const opt = enumOptions.find(
                                            (o) => o.id === value,
                                          );
                                          if (opt) {
                                            handleEnumSelectionByAttrId(
                                              attr.id,
                                              opt.id,
                                            );
                                            handleAttributeChange(
                                              opt.originEntity || '',
                                              opt.originId || '',
                                            );
                                          }
                                        }
                                      }}
                                    >
                                      <SelectTrigger
                                        id={`attr-${attr.id}`}
                                        className="w-full"
                                      >
                                        <SelectValue
                                          placeholder={
                                            attr.customField.fieldName
                                          }
                                        />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {[...enumOptions]
                                          .sort((a, b) =>
                                            (a.name || '').localeCompare(
                                              b.name || '',
                                            ),
                                          )
                                          .map((option) => (
                                            <SelectItem
                                              key={option.id}
                                              value={option.id}
                                            >
                                              {option.name || ''}
                                            </SelectItem>
                                          ))}
                                      </SelectContent>
                                    </ShadcnSelect>
                                    {attr.customField.description && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {attr.customField.description}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <Label
                                      htmlFor={`attr-${attr.id}`}
                                      className="text-sm mb-1 block"
                                    >
                                      {attr.customField.fieldName}
                                      {attr.isRequired && ' *'}
                                    </Label>
                                    <Input
                                      id={`attr-${attr.id}`}
                                      disabled={isLoadingFilteredAttributes}
                                      required={attr.isRequired}
                                      value={
                                        selectedAttributes[
                                          attr.customField.fieldName
                                        ] || ''
                                      }
                                      onChange={(e) => {
                                        handleAttributeChange(
                                          attr.customField.fieldName,
                                          e.target.value,
                                        );
                                      }}
                                    />
                                    {attr.customField.description && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {attr.customField.description}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </Grid>
                            );
                          })}
                        </Grid>

                        {hiddenAttributes.length > 0 && (
                          <div className="mt-1 px-1 flex items-center gap-2">
                            <InfoIcon size={18} />
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              <T context="hidden product attributes list">
                                Hidden:{' '}
                                <Var>
                                  {hiddenAttributes
                                    .map((a) => a.customField.fieldName)
                                    .join(', ')}
                                </Var>
                              </T>
                            </MDTypography>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <QuestionIcon size={16} />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {gt(
                                  "Attributes are hidden because no options are available for the current selection and this customer's registered variants.",
                                  {
                                    context: 'tooltip, hidden attributes',
                                  },
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        )}
                      </MDBox>

                      {/* Show matching variants based on selected attributes */}
                      <MDBox>{renderMatchingVariantsInfo()}</MDBox>
                    </>
                  )}
                </MDBox>
              )}
            </Grid>
          ) : null}

          {/* <Grid item xs={12}>
          <AssociateProductV2
            selectedProductVariantId={selectedVariantId}
            selectedAttributes={selectedAttributes}
            suggestedProductVariantId={lineItem.suggestedProductVariantId}
            topProductVariantMatches={lineItem.topProductVariantMatches}
            onVariantSelected={handleVariantSelect}
          >
            <Grid item xs={12}>
              <span className="text-base font-medium">Customer Request</span>
              <div className="p-2 border border-gray-300 rounded">
                <DataTable<LineItem>
                  table={tableData}
                />
              </div>
            </Grid>
          </AssociateProductV2>
        </Grid> */}
        </Grid>
      </MDBox>
    </Modal>
  );
};

export default AssociateLineItemModal;
